<template>
  <review-item
    :isComplete="isComplete"
    title="Pension Details"
    @edit="$emit('edit')"
  >
    <table
      v-if="accumulation"
      class="w-full table-fixed border-collapse border border-gray-400"
    >
      <thead>
        <tr>
          <th class="py-2 px-2 border uppercase text-gray-600 text-sm text-left" colspan="2">
            Accumulation Account
          </th>
        </tr>
      </thead>
      <tbody v-if="accumulation.amount === 0">
        <tr>
          <td class="px-2 py-1 border uppercase text-gray-600 text-sm text-left" colspan="2">
            N/A
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td class="px-2 py-1 border uppercase text-gray-600 text-sm text-left font-bold">
            Balance
          </td>
          <td class="px-2 py-1 text-left text-gray-600">{{ accumulation.amount | dollar }}</td>
        </tr>
        <tr>
          <td class="px-2 py-1 border uppercase text-gray-600 text-sm text-left font-bold">
            Tax Free Component
          </td>
          <td class="px-2 py-1 border text-left text-gray-600">
            {{ accumulation.components.nonTaxable.value | dollar }}
            <span class="text-xs">
              ({{ accumulation.components.nonTaxable.percent | percent }})
            </span>
          </td>
        </tr>
        <!-- eslint-disable-next-line max-len -->
        <tr v-if="isTris">
          <td class="px-2 py-1 border uppercase text-gray-600 text-sm text-left font-bold">
            Unrestricted Non Preserved
          </td>
          <td class="px-2 py-1 border text-left text-gray-600">
            {{ accumulation.unrestrictedNonPreserve | dollar }}
          </td>
        </tr>
      </tbody>
    </table>
    <table
      class="mt-4 w-full table-fixed border-collapse border border-gray-400"
      v-for="(pension, idx) in pensions"
      :key="idx"
    >
      <thead>
        <tr>
          <th class="py-2 px-2 border uppercase text-gray-600 text-sm text-left" colspan="2">
            Pension Account
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="px-2 py-2 border uppercase text-gray-600 text-sm text-left font-bold">
            Starting Balance
          </td>
          <td v-if="calculatedPensionAmount > 0" class="px-2 py-2 text-left text-gray-600">
            {{ calculatedPensionAmount | dollar }}
          </td>
          <td v-else class="px-2 py-2 text-left text-gray-600">
            N/A
          </td>
        </tr>
        <tr v-if="!isPartOne">
          <td class="px-2 py-2 border uppercase text-gray-600 text-sm text-left font-bold">
            Tax Free Component
          </td>
          <td class="px-2 py-2 border text-left text-gray-600">
            {{ (accumulation.components.nonTaxable.percent * calculatedPensionAmount) | dollar }}
            <span class="text-xs">
              ({{ accumulation.components.nonTaxable.percent | percent }})
            </span>
          </td>
        </tr>
        <!-- eslint-disable-next-line max-len -->
        <tr v-if="isTris">
          <td class="px-2 py-1 border uppercase text-gray-600 text-sm text-left font-bold">
            Unrestricted Non Preserved
          </td>
          <td
            v-if="pension.unrestrictedNonPreserve"
            class="px-2 py-1 border text-left text-gray-600"
          >
            {{ pension.unrestrictedNonPreserve | dollar }}
          </td>
          <td v-else class="px-2 py-2 text-left text-gray-600">
            N/A
          </td>
        </tr>
        <tr>
          <!-- eslint-disable max-len -->
          <td
            class="px-2 py-2 border uppercase text-gray-600 text-sm text-left font-bold align-text-top"
          >
            Reversionary Beneficiary
          </td>
          <!-- eslint-enable max-len -->
          <td class="px-2 py-2 border text-left text-gray-700">
            <p>{{ pension.reversionaryBeneficiary.hasReversionaryBeneficiary }}</p>
            <div v-if="pension.reversionaryBeneficiary.hasReversionaryBeneficiary === 'Yes'">
              <p class="text-sm">
                <span v-if="pension.reversionaryBeneficiary.reversionaryBeneficiary === 'Other'">
                  <!-- eslint-disable-next-line max-len -->
                  <span >Other (other eligible beneficiary)</span>
                  <p class="text-sm">
                    {{ pension.reversionaryBeneficiary.beneficiaryDetails.title }}
                    {{ pension.reversionaryBeneficiary.beneficiaryDetails.firstName }}
                    {{ pension.reversionaryBeneficiary.beneficiaryDetails.lastName }}
                    is the pensioner's {{
                      pension.reversionaryBeneficiary.beneficiaryDetails.otherRelationship
                      ? pension.reversionaryBeneficiary.beneficiaryDetails.otherRelationship
                      : pension.reversionaryBeneficiary.beneficiaryDetails.relationship
                       | lowercase }}
                  </p>
                </span>
                <span v-else>
                  <span >{{ pension.reversionaryBeneficiary.reversionaryBeneficiary }}
                    is the pensioner's {{
                      pension.reversionaryBeneficiary.beneficiaryDetails.otherRelationship
                      ? pension.reversionaryBeneficiary.beneficiaryDetails.otherRelationship
                      : pension.reversionaryBeneficiary.beneficiaryDetails.relationship
                       | lowercase }}
                  </span>
                </span>
              </p>
              <p class="text-sm">Date of Birth: {{
                  pension.reversionaryBeneficiary.beneficiaryDetails.dateOfBirth
                    | moment('DD/MM/YYYY')
                }}
              </p>
            </div>
          </td>
        </tr>
        <tr>
          <!-- eslint-disable max-len -->
          <td
            colspan="2"
            class="px-2 py-2 border uppercase text-gray-600 text-sm text-left font-bold align-text-top"
          >
            Segregation
          </td>
        </tr>
        <tr>
          <!-- eslint-enable max-len -->
          <td class="px-2 py-2 border text-left text-gray-600 text-sm leading-4">
            <p>
              Will any assets be specifically set aside to support the pension?
            </p>
          </td>
          <td class="px-2 py-2 border text-gray-700">
            {{ pension.segregation.hasSegregation }}
          </td>
        </tr>
        <tr v-if="pension.segregation.hasSegregation === 'Yes'">
          <td class="px-2 py-2 border text-gray-600 text-sm leading-4">
            Will any part of the pension be supported from the rest of the fund's (pooled) assets?
          </td>
          <td class="px-2 py-2 border text-gray-700">
            {{ pension.segregation.segregatedPensionAssets }}
          </td>
        </tr>

        <tr v-if="pension.segregation.hasSegregation === 'Yes'">
          <td class="px-2 py-2 border text-gray-600 text-sm leading-4">
            Will these assets also be treated as segregated pension assets for tax purposes?
          </td>
          <td class="px-2 py-2 border text-gray-700">{{ pension.segregation.pensionAssets }}</td>
        </tr>

        <tr>
          <!-- eslint-disable max-len -->
          <td
            colspan="2"
            class="px-2 pt-2 uppercase text-gray-600 text-sm text-left font-bold align-text-top"
          >
            Final Checks
          </td>
        </tr>
        <tr>
          <td class="px-2 pb-2 text-gray-600 text-sm text-left" colspan="2">
            <p>
              The following have been confirmed or acknowledged as being satisfactorily completed if
              applicable:
            </p>
            <ul class="ml-4 list-outside list-disc">
              <li>A review of the Fund’s trust deed</li>
              <li>
                Notices to claim personal tax deductions for contributions that pre-date the pension
              </li>
              <li>Contribution splits between spouses</li>
              <li>Superannuation splitting as part of a separation or divorce</li>
              <li>Up to date valuations of accounts and tax components</li>
              <li>Receipt of all contributions and rollovers included in the pension</li>
            </ul>
          </td>
        </tr>
      </tbody>
    </table>
  </review-item>
</template>

<script>
import toolkit from '@/app/mixins/toolkit/item';
import pensions from '@/app/mixins/toolkit/pension/pension';
import reviewItem from '@/app/modules/toolkit/common/components/review-item/review-item.vue';

export default {
  name: 'pension-review-pension-details',
  mixins: [toolkit, pensions],
  components: { reviewItem },
  props: {
    isComplete: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    setup() {
      return this.getItem.setup;
    },
  },
};
</script>

<style></style>
